import { type MantineColor } from '@mantine/core'
import { truncate } from 'lodash'

export interface VerificationDocumentStatusDisplay {
  label: string
  color: MantineColor
}

// API status → UI presentation, mirroring providerPhotos/statusDisplay.ts so the two
// surfaces read as one system. Labels intentionally differ from the raw Sugar values:
// providers should not see internal-sounding strings like 'invalid'.
// The `| undefined` value type is deliberate: it makes the fallback in the resolver
// below type-meaningful instead of dead code the compiler thinks is unreachable.
export const VERIFICATION_DOCUMENT_STATUS_DISPLAY: Record<string, VerificationDocumentStatusDisplay | undefined> = {
  'Under Review': { label: 'Pending Review', color: 'yellow' },
  'Active': { label: 'Active', color: 'green' },
  'invalid': { label: 'Rejected', color: 'red' },
  'Removed': { label: 'Removed', color: 'gray' }
}

// Unlike the photo equivalent — which looks up a closed union — document status comes
// from the admin-editable `document_status_dom`, so the value set is open (MIQ-2568).
// An unrecognised value renders verbatim in a neutral pill; an absent one renders
// nothing at all, so a pre-MIQ-2568 backend degrades to em-dashes rather than a
// column of blank chips.
export const getVerificationDocumentStatusDisplay = (
  status: string | null | undefined
): VerificationDocumentStatusDisplay | null => {
  if (!status) {
    return null
  }

  // The fallback label renders an admin-authored value verbatim in a `white-space:
  // nowrap; width: 1` cell, so an unbounded string (e.g. 'Rejected - Resubmit With
  // Legible Card') would widen the Status column and squeeze Filename, the table's
  // only flexible column. Cap it; the four mapped labels above are known-short and
  // skip this path entirely.
  return VERIFICATION_DOCUMENT_STATUS_DISPLAY[status] ?? { label: truncate(status, { length: 24 }), color: 'gray' }
}
